import { Layer, LayerType } from 'src/app/shared/map-data-services/layer/layer';
import { MapWorkspace, ShareWorkspaceData } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace';
import { LineStyle, StyleOverride, SymbolOverride } from 'src/app/shared/map-data-services/styles/style-symbology';
import { ChoiceValue, CodedChoiceValue } from 'src/app/shared/template-services/field';
import { Template } from 'src/app/shared/template-services/template';
import { GeoTemplate } from 'src/app/shared/template-services/templateDTO';

export interface WorkspaceDefContentEntry {
    layer: WsdefLayerObject;
    symbol: WsdefSymbolObject | undefined;
    styleOverrides: WsdefStyleOverride[];
    style: WsdefStyleObject | undefined;
    template: GeoTemplate & { hasActiveTasks: boolean };
}

// --- wsdef export types ---
export interface WsdefLayerObject {
    layerName: string;
    sourceLayerName: string;
    layerType: LayerType;
    isInitialLayer: boolean;
    ruleSymbols: any[];
}

export interface WsdefSymbolObject {
    symbolDefinitionUuid: string;
    fillColor: number;
    outlineColor: number;
    text: string;
    color: number;
}

export interface WsdefStyleOverride {
    filter: string;
    styleOverride: StyleOverride;
    symbolOverride: SymbolOverride;
}

export interface WsdefStyleObject {
    name: string;
    image: string;
    lineColor: number;
    lineWeight: number;
    lineStyle: LineStyle;
    fillColor: number;
    fillOpacity: number;
}

export class WorkspaceDefFileBuilder {
    private workspace: MapWorkspace;
    private layers: Layer[];
    private templateDict: { [key: string]: Template };

    constructor(workspace: MapWorkspace, layers: Layer[], templates: Template[]) {
        this.workspace = workspace;
        this.layers = layers;
        this.templateDict = templates.reduce((acc, template) => {
            acc[template.id] = template;
            return acc;
        }, {} as { [key: string]: Template });
    }

    public build(): ShareWorkspaceData {
        return {
            workspace: this.buildWorkspaceData(),
            content: this.layers.map(layer => this.createWsdefContentEntry(this.templateDict[layer.templateId], layer))
        };
    }

    private buildWorkspaceData() {
        return {
            name: this.workspace.name,
            description: this.workspace.description
        };
    }

    private filterValidStyleOverrides(styleOverrides: WsdefStyleOverride[], template: Template): WsdefStyleOverride[] {
        return (styleOverrides || []).filter(override => {
            // Only handle filters of the form $fieldName EQ 'value'
            const match = override.filter && override.filter.match(/^\$(\w+) EQ '(.+)'$/);
            if (!match) return false; // Invalid filter format
            const [, fieldName, value] = match;
            const field = template.fields && template.fields.find(f => f.name === fieldName);
            if (!field) return false; // Field does not exist
            if (field.type === 'Choice' && Array.isArray(field.values)) {
                const choiceValues = field.values as ChoiceValue[];
                return choiceValues.some(v => v.text === value);
            }
            if (field.type === 'CodedChoice' && Array.isArray(field.values)) {
                const codedchoiceValues = field.values as CodedChoiceValue[];
                return codedchoiceValues.some(v => v.code === value);
            }
            return false; // For non-choice fields, return false
        });
    }

    private createWsdefContentEntry(template: Template, layer: Layer): WorkspaceDefContentEntry | null {
        if (!template || !layer) return null;
        // Get the export object from the layer
        const exportObj = layer.toWsdefExport();
        // Use the helper to filter styleOverrides
        const filteredStyleOverrides = this.filterValidStyleOverrides(exportObj.styleOverrides, template);
        return {
            ...exportObj,
            styleOverrides: filteredStyleOverrides,
            template: template.toWsdefTemplateObject()
        };
    }
}
